/**
 * REST client for the anomaly detection pipeline endpoints.
 * Bypasses the project's `httpClient` because that wrapper expects all
 * responses to be wrapped in `{success, data}`, but the anomaly endpoints
 * return raw payloads (e.g. `{plots, columnName, dtAvgH}`).
 */
import i18n from 'i18next';

import { getApiUrl } from '@/config/environment';
import { getAuthToken } from '@/core/auth/supabase-auth';
import { log } from '@/core/debug/anomalyLogger';
import {
  Lang,
  LoadResponse,
  StartResponse,
  ThresholdResponse,
  UseProcessedResponse,
  ValidateParamResponse,
  AnomalyParameters,
  paramsToRequest,
} from './types';
import {
  assertLoadResponse,
  assertStartResponse,
  assertThresholdResponse,
  assertUseProcessedResponse,
  assertValidateParamResponse,
} from './validators';

const BASE = '/api/adjustmentsOfData';

/**
 * Friendly fallback messages when the backend returns a non-2xx status without
 * an `error` field. Backend-supplied messages always override these. Uses
 * i18next directly (not the React hook) so it works outside React context.
 * Keys live in `dataProcessing.json` under `anomaly.errors.httpFallback.*`.
 * Falls back to hardcoded English when i18n is not yet initialised (e.g. in
 * unit tests or very early boot).
 */
function httpStatusFallback(status: number): string {
  if (status === 401) {
    return (
      i18n.t('dataProcessing:anomaly.errors.httpFallback.unauthorized', {
        defaultValue: '',
      }) || 'You are not signed in. Please sign in and retry.'
    );
  }
  if (status === 403) {
    return (
      i18n.t('dataProcessing:anomaly.errors.httpFallback.forbidden', {
        defaultValue: '',
      }) || 'You do not have permission to perform this action.'
    );
  }
  if (status === 404) {
    return (
      i18n.t('dataProcessing:anomaly.errors.httpFallback.notFound', {
        defaultValue: '',
      }) || 'The requested resource was not found.'
    );
  }
  if (status === 409) {
    return (
      i18n.t('dataProcessing:anomaly.errors.httpFallback.conflict', {
        defaultValue: '',
      }) || 'Request conflicts with current pipeline state. Please reload.'
    );
  }
  return (
    i18n.t('dataProcessing:anomaly.errors.httpFallback.default', {
      status,
      defaultValue: '',
    }) || `Request failed (${status}). Please try again.`
  );
}

/** Summarise a response body for logging — avoids flooding console with plot data. */
const safeBody = (body: unknown) => {
  if (!body || typeof body !== 'object') return body;
  const o = body as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(o).map(([k, v]) => [
      k,
      Array.isArray(v)
        ? `[${v.length} items]`
        : typeof v === 'object' && v !== null
          ? '{...}'
          : v,
    ]),
  );
};

async function postJson<T>(
  path: string,
  body: unknown,
  options?: { signal?: AbortSignal },
): Promise<T> {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  log.net('→ POST ' + path, body);
  const t0 = performance.now();
  let res: Response;
  try {
    res = await fetch(getApiUrl(`${BASE}${path}`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: options?.signal,
    });
  } catch (fetchErr) {
    log.err('anomalyApi.postJson ' + path, fetchErr);
    throw fetchErr;
  }
  const duration_ms = Math.round(performance.now() - t0);
  const text = await res.text();
  let parsed: unknown = null;
  let parseError: Error | null = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parseError = new Error(`Invalid JSON response from ${path}: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    const parsedObj = parsed as { error?: string; error_code?: string; suggestions?: string[] } | null;
    const backendErr = parsedObj?.error;
    const backendCode = parsedObj?.error_code;
    const backendSuggestion = parsedObj?.suggestions?.[0];
    const fallback = httpStatusFallback(res.status);
    const errMsg = (backendErr && backendErr.trim()) || fallback;
    log.err('anomalyApi.postJson ' + path, { errMsg, backendCode });
    const err = new Error(errMsg) as Error & { code?: string; status?: number; suggestion?: string };
    err.status = res.status;
    if (backendCode) err.code = backendCode;
    if (backendSuggestion) err.suggestion = backendSuggestion;
    throw err;
  }
  if (parseError) throw parseError;
  log.net('← ' + res.status + ' ' + path + ' (' + duration_ms + 'ms)', safeBody(parsed));
  return parsed as T;
}

export const anomalyApi = {
  async load(uploadId: string, filename: string, lang: Lang): Promise<LoadResponse> {
    const out = await postJson<unknown>('/load', { uploadId, filename, lang });
    assertLoadResponse(out);
    return out;
  },

  async validateParam(
    name: string,
    value: unknown,
    currentParams: ReturnType<typeof paramsToRequest>,
    lang: Lang,
    uploadId?: string,
    options?: { signal?: AbortSignal },
  ): Promise<ValidateParamResponse> {
    const out = await postJson<unknown>(
      '/validate-param',
      { name, value, currentParams, lang, uploadId },
      options,
    );
    assertValidateParamResponse(out);
    return out;
  },

  async start(
    uploadId: string,
    params: AnomalyParameters,
    lang: Lang,
    options?: { signal?: AbortSignal },
  ): Promise<StartResponse> {
    const out = await postJson<unknown>(
      '/start',
      { uploadId, params: paramsToRequest(params), lang },
      options,
    );
    assertStartResponse(out);
    return out;
  },

  async stlThreshold(
    uploadId: string,
    threshold: number,
    lang: Lang,
    options?: { signal?: AbortSignal },
  ): Promise<ThresholdResponse> {
    const out = await postJson<unknown>(
      '/stl-threshold',
      { uploadId, threshold, lang },
      options,
    );
    assertThresholdResponse(out, '/stl-threshold');
    return out;
  },

  async lstmThreshold(
    uploadId: string,
    threshold: number,
    lang: Lang,
    options?: { signal?: AbortSignal },
  ): Promise<ThresholdResponse> {
    const out = await postJson<unknown>(
      '/lstm-threshold',
      { uploadId, threshold, lang },
      options,
    );
    assertThresholdResponse(out, '/lstm-threshold');
    return out;
  },

  async useProcessed(uploadId: string, lang: Lang): Promise<UseProcessedResponse> {
    const out = await postJson<unknown>('/use-processed', { uploadId, lang });
    assertUseProcessedResponse(out);
    return out;
  },

  async cancel(uploadId: string): Promise<void> {
    // Fire-and-forget — server is idempotent (returns 200 on already-idle,
    // 404 on unknown upload). Never throw to caller; cancel is best-effort
    // cleanup so the user never sees a secondary error after cancellation.
    log.net('→ POST /cancel-pipeline', { uploadId });
    try {
      const token = await getAuthToken();
      if (!token) return;
      await fetch(getApiUrl(`${BASE}/cancel-pipeline`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uploadId }),
      });
      log.net('← cancel-pipeline sent (fire-and-forget)', { uploadId });
    } catch {
      // Intentionally swallowed — cancel is best-effort
    }
  },
};
